import {
  reconcileBiometricUser,
  clearPersistedNativeSession,
  persistNativePrimarySession,
} from "@features/BiometricAuthentication/biometricUnlock";
import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useState } from "react";
import { Platform } from "react-native";

import { whooshPatronUserIdentityForExternalTools } from "@/hooks/useIdentifyUserToExternalTools/types";
import { useIdentifyUserToExternalTools } from "@/hooks/useIdentifyUserToExternalTools/useIdentifyUserToExternalTools";
import { readAnalyticsTrackingPermissionSession } from "@/utils/Analytics/useSyncAnalyticsTrackingPermission";
import { buildStaffAppUrl } from "@/utils/getStaffAppUrlString";
import { useFlags } from "@/utils/launchDarkly";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  clearNativeSessionSignedOut,
  resumeNativeSessionPersistence,
} from "@whoosh/relay/src";

import {
  WhooshMutationError,
  useMutationWithError,
} from "../../../utils/hooks/useMutationWithError";
import { currentProfileAtom } from "../../Club/atoms";
import { sessionAtom } from "../../Session";
import { isUserAuthenticated_atom } from "../atoms";
import {
  CreateSessionInput,
  useSignInMutation,
  useSignInMutation$data,
} from "./__generated__/useSignInMutation.graphql";

// The session needs to match the fragment in the SetSession hook.
export const SignInMutationString = graphql`
  mutation useSignInMutation($input: CreateSessionInput!) {
    responsePayload: createSession(input: $input) {
      userType
      success
      session {
        ...useSyncAnalyticsTrackingPermission_session
        currentClientProfile {
          ... on MemberProfile {
            id
            active
          }
          member {
            club {
              name
              id
            }
            ... on Client {
              __typename
              ... on Customer {
                departmentMemberProfiles(type: GOLF) {
                  ... on GolfMemberProfile {
                    department {
                      slug
                    }
                    ghinNumber
                  }
                }
                membershipType {
                  id
                }
                __typename
                id
                firstName
                lastName
                fullName
                email
                phoneNumber {
                  nationalNumber
                }
                profileImage {
                  # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                  publicId: renderingKey
                }
              }
              ... on Member {
                departmentMemberProfiles(type: GOLF) {
                  ... on GolfMemberProfile {
                    department {
                      slug
                    }
                    ghinNumber
                  }
                }
                __typename
                id
                firstName
                lastName
                fullName
                email
                phoneNumber {
                  nationalNumber
                }
                profileImage {
                  # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                  publicId: renderingKey
                }
                membershipType {
                  id
                }
                memberGroups {
                  id
                }
              }
            }
          }
        }
        masquerader {
          email
          username
        }
        user {
          id
          email
          username
          ... on MemberUser {
            memberProfiles(active: true) {
              edges {
                node {
                  member {
                    club {
                      id
                      name
                      slug
                      supportsPublic
                      logoImage {
                        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                        publicId: renderingKey
                      }
                    }
                  }
                }
              }
            }
            metadata {
              hasSeenNotificationNudgeModal
            }
          }
        }
      }
      errors {
        code

        message
        field
      }
    }
  }
`;

export const useSignIn = () => {
  const { biometricAuth } = useFlags();
  const [tryLogin, isLoggingIn] =
    useMutationWithError<useSignInMutation>(SignInMutationString);
  const setSessionAtoms = useSetSessionAtoms();
  const [hasError, setHasError] = useState(false);
  const { showToast } = useToast();
  const identifyUserToExternalTools = useIdentifyUserToExternalTools();
  const onCompleted = async (
    res: useSignInMutation$data,
    successCallback: (res: useSignInMutation$data) => void,
    errorCallback?: (errors: any) => void
  ) => {
    if (res.responsePayload?.success) {
      if (Platform.OS !== "web") {
        const authenticatedUserId = res.responsePayload?.session?.user?.id;
        if (authenticatedUserId && biometricAuth === true) {
          await reconcileBiometricUser(authenticatedUserId);
        }
        await persistNativePrimarySession();
        await clearNativeSessionSignedOut();
      }

      setSessionAtoms(res);
      const { identity, currentClub } = getUserIdentityForExternalTools(res);
      identifyUserToExternalTools({
        user: identity,
        currentClub,
      });
      successCallback(res);
    } else {
      errorCallback && errorCallback(res.responsePayload?.errors);
    }
  };

  const onCompletedErrorCustom = (errors: any) => {
    setHasError(true);
    let description = t`Whoops. Looks like we don't recognize those credentials.`;
    if (Array.isArray(errors)) {
      errors.forEach((error) => {
        if (error?.extensions?.code === "DEACTIVATED") {
          description =
            "Access to the club has been removed. Please contact club for assistance.";
        }
      });
    }

    showToast({
      description,
      semanticStyle: "error",
      toastId: "loginError",
    });
    return [];
  };

  const doLogin = (
    input: CreateSessionInput,
    successCallback: (res: useSignInMutation$data) => void,
    errorCallback?: (errors: WhooshMutationError[]) => void
  ) => {
    const startLogin = () => {
      if (Platform.OS !== "web") {
        resumeNativeSessionPersistence();
      }

      tryLogin({
        variables: { input },
        onCompleted: (res) => onCompleted(res, successCallback, errorCallback),
        onCompletedErrorCustom: (errors) => onCompletedErrorCustom(errors),
        onPayloadError: (errors) => {
          onCompletedErrorCustom(errors);
        },
      });
    };

    startLogin();
  };

  return [doLogin, isLoggingIn, hasError] as const;
};

const getUserIdentityForExternalTools = (data: useSignInMutation$data) => {
  const analyticsTrackingSession = readAnalyticsTrackingPermissionSession(
    data.responsePayload?.session
  );
  const user = data.responsePayload?.session?.user;
  const currentProfile = data.responsePayload?.session?.currentClientProfile;
  const currentClubNode = currentProfile?.member?.club;
  const activeClubs =
    user?.memberProfiles?.edges?.map((edge) => ({
      id: edge?.node?.member?.club?.id ?? "",
      name: edge?.node?.member?.club?.name ?? "",
    })) ?? [];

  const identity: whooshPatronUserIdentityForExternalTools = {
    userId: user?.id ?? "",
    email: user?.email ?? "",
    name: user?.username ?? "",
    activeClubs,
    requiresAnalyticsTrackingConsent:
      analyticsTrackingSession?.requiresAnalyticsTrackingConsent,
    analyticsTrackingConsentedAt:
      analyticsTrackingSession?.user?.__typename === "MemberUser"
        ? analyticsTrackingSession.user.metadata?.analyticsTrackingConsentedAt
        : null,
  };
  const currentClub = {
    id: currentClubNode?.id ?? "",
    name: currentClubNode?.name ?? "",
  };
  return {
    identity,
    currentClub,
  };
};

export const useSetSessionAtoms = () => {
  const setSession = useSetAtom(sessionAtom);
  const setCurrentProfile = useSetAtom(currentProfileAtom);
  const setIsUserAuthenticated = useSetAtom(isUserAuthenticated_atom);
  const { showToast } = useToast();

  const setSessionAtoms = (data: useSignInMutation$data) => {
    if (data.responsePayload?.userType === "MEMBER") {
      const user = data.responsePayload?.session?.user;
      const currentProfile =
        data.responsePayload?.session?.currentClientProfile;
      if (user) {
        setSession({
          user: {
            id: user.id,
            email: user.email!,
            username: user.username!,
            clubs:
              user.memberProfiles?.edges?.map((edge) => ({
                id: edge?.node?.member?.club?.id || "",
                slug: edge?.node?.member?.club?.slug || "",
                name: edge?.node?.member?.club?.name || "",
                logoImage: edge?.node?.member?.club?.logoImage?.publicId || "",
                supportsPublic: edge?.node?.member?.club?.supportsPublic,
              })) || [],
          },
        });
        if (
          currentProfile?.member?.__typename === "Member" ||
          currentProfile?.member?.__typename === "Customer"
        ) {
          const ghinNumber =
            currentProfile?.member?.departmentMemberProfiles?.find(
              (dmp) => !!dmp?.ghinNumber
            )?.ghinNumber;
          setCurrentProfile({
            memberId: currentProfile?.member?.id || "",
            membershipTypeId: currentProfile?.member?.membershipType?.id || "",
            firstName: currentProfile?.member?.firstName || "",
            lastName: currentProfile?.member?.lastName || "",
            memberGroups: [],
            hasSeenNotificationNudgeModal:
              user?.metadata?.hasSeenNotificationNudgeModal || false,
            email: currentProfile?.member?.email || "",
            phoneNumber:
              currentProfile?.member?.phoneNumber?.nationalNumber || "",
            ghinNumber,
          });
        } else {
          setCurrentProfile(null);
        }
      }
    } else {
      if (Platform.OS === "web") {
        window.location.href = buildStaffAppUrl(window.location);
      } else {
        clearPersistedNativeSession().then(() => {
          setIsUserAuthenticated(false);
          showToast({
            description: t`Whoops! For now, only members can log into the mobile application.`,
            semanticStyle: "error",
            toastId: "staff-login-error",
          });
        });
      }
    }
  };

  return setSessionAtoms;
};
